/**
 * Helper for the branch-scoped activity-logs API (tecfit-backend).
 * Backend route: POST /api/v1/system/branches/:branchId/activity-logs (CreateActivityLogDto).
 *
 * Callers typically pass a full member/customer object augmented with
 * `type`, `scope`, `initiatedFrom`, `currentUserData`. The backend
 * ValidationPipe runs with `forbidNonWhitelisted`, so everything outside
 * CreateActivityLogDto (and CurrentUserDataDto) must be stripped.
 */

function pickFeeDetail(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const f = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  if (f.fee !== undefined && f.fee !== null) out.fee = f.fee;
  if (f.discount !== undefined && f.discount !== null) out.discount = f.discount;
  if (f.tax !== undefined && f.tax !== null) out.tax = f.tax;
  if (f.pay !== undefined && f.pay !== null) out.pay = f.pay;
  return Object.keys(out).length ? out : undefined;
}

function pickCurrentUserData(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const u = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  if (u._id) out._id = String(u._id);
  if (u.name) out.name = String(u.name);
  if (u.company) out.company = String(u.company);
  if (u.branch) out.branch = String(u.branch);
  return Object.keys(out).length ? out : undefined;
}

const STRING_FIELDS = [
  '_id',
  'name',
  'tagNumber',
  'scope',
  'initiatedFrom',
  'company',
  'branch',
  'activityBy',
  'createdBy',
  'userCode',
  'customerID',
  'packageName',
  'memberName',
  'requestId',
  'amount',
  'subject',
  'subscriberMembership',
  'subscriberName',
  'subscriberType',
  'subscriberId',
  'feeDate',
  'oldValue',
  'newValue',
  'status',
  'gymName',
] as const;

/** Body for POST .../system/branches/:branchId/activity-logs (CreateActivityLogDto). */
export function buildActivityLogPayload(raw: Record<string, unknown>): Record<string, unknown> {
  const r = (raw ?? {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    type: r.type !== undefined && r.type !== null ? String(r.type) : '',
  };

  for (const key of STRING_FIELDS) {
    const value = r[key];
    if (value !== undefined && value !== null && value !== '') {
      payload[key] = String(value);
    }
  }

  const currentUserData = pickCurrentUserData(r.currentUserData);
  if (currentUserData) payload.currentUserData = currentUserData;

  const feeDetail = pickFeeDetail(r.feeDetail);
  if (feeDetail) payload.feeDetail = feeDetail;
  const fund = pickFeeDetail(r.fund);
  if (fund) payload.fund = fund;
  const fee = pickFeeDetail(r.fee);
  if (fee) payload.fee = fee;

  if (r.pay !== undefined && r.pay !== null) payload.pay = r.pay;
  if (r.discount !== undefined && r.discount !== null) payload.discount = r.discount;
  if (r.tax !== undefined && r.tax !== null) payload.tax = r.tax;

  if (r.service && typeof r.service === 'object') {
    const s = r.service as Record<string, unknown>;
    if (s.name) payload.service = { name: String(s.name) };
  }

  return payload;
}
